// Centralized constants for STUDLYF AI landing page
export const LOGO_URL = "/studlyf-logo.png";

export const NAV_LINKS = [
  { label: "Home", href: "/#home" },
  { label: "About Us", href: "/about" },
  { label: "Discover", href: "/discover" },
  { label: "Workspace", href: "/workspace" },
  { label: "Pricing", href: "/#pricing" },
  { label: "Contact", href: "/contact" },
];

export const PLATFORM_FEATURES = [
  { title: "Business Foundations", desc: "Build a rock-solid base — vision, mission, model and metrics defined by AI.", icon: "Building2", gradient: "from-[#7C3AED] to-[#EC4899]" },
  { title: "Strategy Planning", desc: "SWOT, Porter, Lean Canvas, Blue Ocean — generated, refined and ready.", icon: "Compass", gradient: "from-[#EC4899] to-[#A855F7]" },
  { title: "Market Intelligence", desc: "Live competitor scans, TAM/SAM/SOM and trend reports in seconds.", icon: "Globe2", gradient: "from-[#6366F1] to-[#7C3AED]" },
  { title: "Brand Builder", desc: "Identity, voice, taglines and positioning that feel hand-crafted.", icon: "Palette", gradient: "from-[#A855F7] to-[#EC4899]" },
  { title: "Marketing Studio", desc: "Campaigns, content pillars, social posts and email sequences on demand.", icon: "Megaphone", gradient: "from-[#22C55E] to-[#6366F1]" },
  { title: "Sales Planner", desc: "Funnels, scripts, CRM workflows and KPIs your team will actually use.", icon: "Target", gradient: "from-[#EC4899] to-[#7C3AED]" },
  { title: "Investor Toolkit", desc: "Pitch decks, one-pagers, VC scores and data rooms built for raises.", icon: "PiggyBank", gradient: "from-[#F59E0B] to-[#A855F7]" },
  { title: "Knowledge Hub", desc: "A curated AI library of frameworks, playbooks and templates.", icon: "BookOpen", gradient: "from-[#7C3AED] to-[#6366F1]" },
  { title: "AI Chat", desc: "Talk to a strategist 24/7 — context-aware, fast and never tired.", icon: "MessageSquare", gradient: "from-[#A855F7] to-[#F59E0B]" },
  { title: "Multi-Agent Workspace", desc: "Ten specialist agents collaborate on your startup in parallel.", icon: "Users", gradient: "from-[#EC4899] to-[#22C55E]" },
  { title: "Document Generator", desc: "From contracts to investor reports — exportable to PDF, DOCX, PPTX.", icon: "FileText", gradient: "from-[#6366F1] to-[#EC4899]" },
  { title: "Analytics Dashboard", desc: "One canvas. Every metric. Real-time. Beautifully visualised.", icon: "LineChart", gradient: "from-[#22C55E] to-[#7C3AED]" },
];

export const SOLUTIONS = [
  {
    title: "STUDLYF for Startups",
    sub: "Launch smarter. Grow faster with AI.",
    icon: "Rocket",
    gradient: "from-[#7C3AED] to-[#EC4899]",
    features: ["Validate business ideas", "Build business foundations", "Market research", "GTM planning", "Investor readiness"],
  },
  {
    title: "STUDLYF for Pre-Incubators",
    sub: "Turn student ideas into startups with AI.",
    icon: "GraduationCap",
    gradient: "from-[#6366F1] to-[#7C3AED]",
    features: ["Startup learning paths", "Business validation", "Pitch preparation", "Faculty dashboard", "Progress tracking"],
  },
  {
    title: "STUDLYF for Incubators",
    sub: "Accelerate every cohort with AI.",
    icon: "Building",
    gradient: "from-[#EC4899] to-[#A855F7]",
    features: ["Portfolio management", "Cohort dashboard", "Mentor collaboration", "Funding readiness", "Demo day prep"],
  },
  {
    title: "STUDLYF for Mentors",
    sub: "Mentor faster with AI-powered preparation.",
    icon: "UserCheck",
    gradient: "from-[#22C55E] to-[#6366F1]",
    features: ["Startup diagnostics", "AI mentor insights", "SWOT reports", "Session prep", "Startup scoring"],
  },
  {
    title: "STUDLYF for Agencies",
    sub: "Deliver more. Burn out less with AI.",
    icon: "Briefcase",
    gradient: "from-[#A855F7] to-[#F59E0B]",
    features: ["Client strategy", "Marketing campaigns", "Brand creation", "SEO planning", "White-label reports"],
  },
  {
    title: "STUDLYF for Investors & VCs",
    sub: "Assess faster. Back smarter. Grow stronger.",
    icon: "TrendingUp",
    gradient: "from-[#F59E0B] to-[#EC4899]",
    features: ["Startup evaluation", "VC score", "Due diligence", "Founder assessment", "Portfolio insights"],
  },
];

export const WORKFLOW_STEPS = [
  { label: "Idea", icon: "Lightbulb", color: "#7C3AED" },
  { label: "Validation", icon: "ShieldCheck", color: "#EC4899" },
  { label: "Market Research", icon: "Globe2", color: "#6366F1" },
  { label: "Strategy", icon: "Compass", color: "#A855F7" },
  { label: "Brand", icon: "Palette", color: "#EC4899" },
  { label: "Marketing", icon: "Megaphone", color: "#22C55E" },
  { label: "Investor", icon: "PiggyBank", color: "#F59E0B" },
  { label: "Launch", icon: "Rocket", color: "#7C3AED" },
  { label: "Growth", icon: "TrendingUp", color: "#EC4899" },
];

export const AI_AGENTS = [
  { name: "Business Agent", role: "Strategy & operations", icon: "Briefcase", color: "#7C3AED" },
  { name: "Research Agent", role: "Market intelligence", icon: "Search", color: "#6366F1" },
  { name: "Marketing Agent", role: "Campaigns & growth", icon: "Megaphone", color: "#EC4899" },
  { name: "SEO Agent", role: "Search & content", icon: "Globe2", color: "#22C55E" },
  { name: "Brand Agent", role: "Identity & voice", icon: "Palette", color: "#A855F7" },
  { name: "Sales Agent", role: "Funnels & deals", icon: "Target", color: "#EC4899" },
  { name: "Investor Agent", role: "Decks & VC score", icon: "PiggyBank", color: "#F59E0B" },
  { name: "Content Agent", role: "Copy & storytelling", icon: "PenLine", color: "#7C3AED" },
  { name: "Finance Agent", role: "Models & forecasts", icon: "Calculator", color: "#22C55E" },
  { name: "Analytics Agent", role: "Insights & KPIs", icon: "LineChart", color: "#6366F1" },
];

export const METRICS = [
  { value: 25, suffix: "K+", label: "Students Reached" },
  { value: 30, suffix: "+", label: "Institutions Collaboration" },
  { value: 1, suffix: " Lakh+", label: "Students Engaged" },
  { value: 10, suffix: "+", label: "Startups Supported" },
];

export const TESTIMONIALS = [
  { name: "Ananya Verma", role: "Founder, Lumen Labs", quote: "STUDLYF AI rewrote our GTM in an afternoon. Investors finally got what we do.", rating: 5, avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=300&q=80" },
  { name: "Rohan Sharma", role: "Partner, North Ventures", quote: "The VC Score saves my team hours on every diligence call. It's our new first read.", rating: 5, avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=300&q=80" },
  { name: "Priya Raman", role: "Head of Incubation, BITS", quote: "We onboarded 40 student startups in a week — every cohort report writes itself.", rating: 5, avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=300&q=80" },
];

export const PRICING = [
  {
    name: "Starter", monthly: 0, yearly: 0, badge: "Free forever",
    features: ["1 project", "5 AI generations / day", "Strategy basics", "Community support"],
    cta: "Start free",
  },
  {
    name: "Pro", monthly: 29, yearly: 24, badge: "Most popular", highlight: true,
    features: ["5 projects", "Unlimited AI strategy", "Brand + Marketing studio", "Pitch deck export", "Priority support"],
    cta: "Go Pro",
  },
  {
    name: "Business", monthly: 79, yearly: 64,
    features: ["20 projects", "All 10 agents", "VC Score + diligence", "White-label exports", "Dedicated success"],
    cta: "Start Business",
  },
  {
    name: "Enterprise", monthly: null, yearly: null, badge: "Custom",
    features: ["Unlimited projects", "Custom agents & SLA", "SSO & audit logs", "On-prem deploy", "Solutions architect"],
    cta: "Talk to sales",
  },
];

export const FAQ_ITEMS = [
  { q: "What exactly does STUDLYF AI do?", a: "STUDLYF AI is a complete business growth platform — it generates strategy, market research, branding, marketing assets, sales plans and investor-ready decks using a team of specialised AI agents." },
  { q: "Who is STUDLYF AI for?", a: "Founders, students, startup teams, incubators, accelerators, mentors, agencies, SMEs and investors. Each audience gets a tailored workspace." },
  { q: "Do I need any prior business knowledge?", a: "No. Our AI walks you from idea to launch with structured prompts and frameworks. You bring the vision — we bring the playbook." },
  { q: "How accurate is the market data?", a: "We blend live web research with curated knowledge bases and validated frameworks. Every insight is traceable and exportable." },
  { q: "Can I export my work?", a: "Yes — decks (PPTX/PDF/Google Slides), documents (DOCX/PDF), and dashboards (CSV/PNG)." },
  { q: "Is my data secure?", a: "All workspaces are isolated, encrypted at rest and in transit, and never used to train public models." },
];

export const BLOG_POSTS = [
  { title: "From idea to pitch deck in 47 minutes", excerpt: "How a solo founder used STUDLYF AI to land a pre-seed round.", tag: "Case study", img: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZSUyMG1pbmltYWx8ZW58MHx8fHwxNzgyNjMyMTAxfDA&ixlib=rb-4.1.0&q=85" },
  { title: "The new VC due-diligence stack", excerpt: "Why investors are flipping their first-read process around AI.", tag: "Insights", img: "https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZSUyMG1pbmltYWx8ZW58MHx8fHwxNzgyNjMyMTAxfDA&ixlib=rb-4.1.0&q=85" },
  { title: "10 strategy frameworks every founder needs", excerpt: "We rebuilt them as one-click AI prompts inside STUDLYF.", tag: "Playbook", img: "https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZSUyMG1pbmltYWx8ZW58MHx8fHwxNzgyNjMyMTAxfDA&ixlib=rb-4.1.0&q=85" },
];

export const FOOTER_LINKS = {
  Company: ["About", "Careers", "Discover", "Pricing", "Blog", "Resources", "Startup Directory", "Contact"],
  Community: ["LinkedIn", "Instagram", "YouTube", "Twitter", "Newsletter"],
};
